import { Flex, Typography } from '@aloft/toolkit-sui';

import { FEATURES } from '../../../redux/constants.ts';
import { selectImprovementsById, selectImprovementsDisplay } from '../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { selectOrderFeatures } from '../../../redux/features/selectors/orderSelectors.ts';
import { selectBathsCostDisplay } from '../../../redux/features/selectors/costSelectors/selectBathsCost.ts';
import { selectFoundationCostDisplay } from '../../../redux/features/selectors/costSelectors/selectFoundationCost.ts';
import { selectManufacturedGlaAndFixedCostDisplay } from '../../../redux/features/selectors/costSelectors/selectManufacturedCost.ts';
import {
  selectMultiFamilyGBACostDisplay,
  selectMultiFamilyUnitsCostDisplay,
} from '../../../redux/features/selectors/costSelectors/selectMultiFamilyCost.ts';
import { selectSFRCostDisplay } from '../../../redux/features/selectors/costSelectors/selectSFRCost.ts';
import { selectCostNewDisplay } from '../../../redux/features/selectors/selectCostNew.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { ContentBlock } from '../../../components/Analysis/ColumnComponents/ContentBlock.tsx';
import { DetailLineWithValue } from '../../../components/Analysis/ColumnComponents/DetailLineWithValue.tsx';
import { TotalLine } from '../../../components/Analysis/ColumnComponents/TotalLine.tsx';
import { FEATURE_COST_SELECTORS } from '../../analysis/ColumnSections/Improvements/featureCostSelectors.ts';
import { PLACEHOLDER } from '../../analysis/consts.ts';

type Line = { text?: string; cost?: string };

const buildFeatureLineText = (feature: keyof typeof FEATURES, improvements: unknown) => {
  const featureDef = FEATURES[feature];
  const base = featureDef.label;
  const details = featureDef.getDetails?.(improvements as never) ?? '';
  return details.trim().length > 0 ? `${base} ${details}` : base;
};

export const CostNewBreakdown = ({
  id,
  featurePresenceIds,
}: {
  id: string;
  featurePresenceIds?: string[];
}) => {
  const { improvements, improvementsDisplay, orderFeatures } = useAppSelector((state) => ({
    improvements: selectImprovementsById(state, id),
    improvementsDisplay: selectImprovementsDisplay(state, id),
    orderFeatures: selectOrderFeatures(state),
  }));

  const presenceIds = featurePresenceIds?.length ? featurePresenceIds : [id];
  const presenceImprovements = useAppSelector((state) =>
    presenceIds.map((pid) => selectImprovementsById(state, pid)),
  );

  const {
    costNewTotal,
    sfrGlaCost,
    multiFamilyGbaCost,
    multiFamilyUnitsCost,
    manufacturedFoundationCost,
    manufacturedGlaCost,
    bathsCost,
  } = useAppSelector((state) => ({
    costNewTotal: selectCostNewDisplay(state, id),
    sfrGlaCost: selectSFRCostDisplay(state, id),
    multiFamilyGbaCost: selectMultiFamilyGBACostDisplay(state, id),
    multiFamilyUnitsCost: selectMultiFamilyUnitsCostDisplay(state, id),
    manufacturedFoundationCost: selectFoundationCostDisplay(state, id),
    manufacturedGlaCost: selectManufacturedGlaAndFixedCostDisplay(state, id),
    bathsCost: selectBathsCostDisplay(state, id),
  }));

  const mainLines: Line[] = [];
  if (improvements?.propertyType === 'multiFamily') {
    mainLines.push(
      { text: improvementsDisplay.gla, cost: multiFamilyGbaCost },
      { text: improvementsDisplay.numberOfUnits, cost: multiFamilyUnitsCost },
      { text: improvementsDisplay.baths, cost: bathsCost },
    );
  } else if (improvements?.propertyType === 'manufacturedHome') {
    mainLines.push(
      { text: improvementsDisplay.foundation, cost: manufacturedFoundationCost },
      { text: improvementsDisplay.gla, cost: manufacturedGlaCost },
      { text: improvementsDisplay.baths, cost: bathsCost },
    );
  } else {
    mainLines.push(
      { text: improvementsDisplay.gla, cost: sfrGlaCost },
      { text: improvementsDisplay.baths, cost: bathsCost },
    );
  }

  const allFeatureKeys = (orderFeatures ?? [])
    .filter((feature) => feature in FEATURE_COST_SELECTORS)
    .filter((feature) => feature in FEATURES) as (keyof typeof FEATURE_COST_SELECTORS)[];

  const featureKeys = allFeatureKeys.filter((feature) => {
    const def = FEATURES[feature as keyof typeof FEATURES];
    if (!def?.hasFeature) return true;
    return presenceImprovements.some((imp) => imp && def.hasFeature?.(imp));
  });

  const featureCosts = useAppSelector((state) =>
    Object.fromEntries(
      featureKeys.map((feature) => [feature, FEATURE_COST_SELECTORS[feature](state, id)] as const),
    ) as Record<string, string | undefined>,
  );

  const featureLines: Line[] = featureKeys.map((feature) => {
    const def = FEATURES[feature as keyof typeof FEATURES];
    const base = def?.label ?? '';
    const hasFeature = def?.hasFeature ? improvements && def.hasFeature(improvements) : true;

    if (!hasFeature) return { text: base, cost: base.length > 0 ? `No ${base}` : '' };

    return {
      text: buildFeatureLineText(feature as keyof typeof FEATURES, improvements),
      cost: featureCosts[feature],
    };
  });

  return (
    <ContentBlock>
      <Flex justifyContent="space-between" alignItems="baseline" gap="1rem">
        <Typography variant="h4" color="textPrimary">
          Cost New
        </Typography>
        <Typography variant="h4" color="textPrimary">
          {costNewTotal ?? PLACEHOLDER}
        </Typography>
      </Flex>
      <Flex flexDirection="column" marginTop="0.5rem" gap="0.25rem">
        {mainLines
          .filter((l) => (l.text ?? '').trim().length > 0)
          .map((line) => (
            <DetailLineWithValue
              key={`${id}-main-${line.text ?? ''}`}
              text={line.text ?? ''}
              value={line.cost ?? ''}
              textColor="textTertiary"
              valueColor="textTertiary"
            />
          ))}
        {featureLines.map((line) => (
          <DetailLineWithValue
            key={`${id}-feat-${line.text ?? ''}`}
            text={line.text ?? ''}
            value={line.cost ?? ''}
            textColor="textTertiary"
            valueColor="textTertiary"
          />
        ))}
      </Flex>
    </ContentBlock>
  );
};
